import {SuccessPageTimelineStepStatus} from '@/components/common/success-page/SuccessPageNextSteps'
import {Locale, TrademarkType, VerificationTrademarkTypeValue} from '@/utils/constants'
import {routeTo} from '@/utils/router'

import {getContactContent} from './contact'
import {getTrademarkVerificationContent} from './trademarkVerificationContent'

export const useTrademarkVerificationContent = () => {
  const {consultationLinkHref} = getContactContent()

  return {
    ...getTrademarkVerificationContent(),
    lawyerCheckTitle: 'lawyer-check-title',
    freeLabel: 'ver-price-free',
    editButton: 'button-edit',
    continueButtonText: 'button-continue',
    submitButtonText: 'forms.button-send-request',
    markets: {
      title: 'verification-markets-selection-title',
      feesDescription: 'verification-markets-selection-desc',
      selection: {
        title: 'verification-all-markets-modal-title',
        description: 'verification-markets-selection-list-desc',
        selectButtonText: 'button-select-markets',
        addMoreButtonText: 'button-add-more-markets',
      },
      euPriceDescription: 'offering-card-included-in-eu-desc',
      allMarketsButton: {
        title: 'verification-all-markets-button-title',
        description: 'verification-all-markets-button-desc',
      },
      modalTitle: 'verification-all-markets-modal-title',
      modalSearchPlaceholder: 'search-country-placeholder',
      alreadyCovered: 'already-covered',
      noResultsTitle: 'verification-all-markets-no-results-title',
      contactLinkText: 'button-contact-us',
      contactLinkHref: consultationLinkHref,
      notListedDescription: 'verification-not-listed-markets-title',
      benefits: {
        title: 'verification-markets-included-benefits-title',
        list: [
          {
            title: 'pricing-reg-fee-contains-1',
            description: 'pricing-reg-fee-contains-1-desc',
          },
          {
            title: 'pricing-reg-fee-contains-0',
            description: 'pricing-reg-fee-contains-0-desc',
          },
          {
            title: 'pricing-reg-fee-contains-4',
            description: 'pricing-reg-fee-contains-4-desc',
          },
          {
            title: 'pricing-reg-fee-contains-3',
            description: 'pricing-reg-fee-contains-3-desc',
          },
          {
            title: 'pricing-reg-fee-contains-2',
            description: 'pricing-reg-fee-contains-2-desc',
          },
          {
            title: 'pricing-reg-fee-contains-5',
            description: 'pricing-reg-fee-contains-5-desc',
          },
        ],
        filingFeesDisclaimer: 'pricing-filing-fees-disclaimer',
      },
      discount: {
        imageSrc: '/images/discount.svg',
        imageAlt: 'Discount',
        title: 'verification-markets-discount-title',
        description: 'verification-markets-discount-desc',
      },
      euipo: {
        imageSrc: '/images/euipo-logo.svg',
        imageAlt: 'EUIPO logo',
        title: 'verification-euipo-grant-title',
        description: 'verification-euipo-grant-description',
        learnMoreLinkHref: {
          [Locale.en]: routeTo.blog.industryArticle(
            'startups',
            'sme-fund-2026-reduce-your-trademark-costs-and-secure-your-brand',
          ),
          [Locale.sk]: routeTo.blog.industryArticle(
            'startups',
            'euipo-grant-2026-75-zlava-pre-registraciu-znacky',
          ),
          [Locale.cs]: routeTo.blog.industryArticle(
            'startups',
            'euipo-grant-2026-75-sleva-pro-registraci-znacky',
          ),
          [Locale.es]: routeTo.blog.industryArticle(
            'startups',
            'subvencion-euipo-2026-75-de-descuento-para-el-registro-de-marca',
          ),
        } as Partial<Record<Locale, string>>,
        checkboxTitle: 'verification-euipo-grant-checkbox-title',
        checkboxPrice: 'verification-euipo-grant-checkbox-price',
        modal: {
          title: 'verification-euipo-grant-modal-title',
          description: 'verification-euipo-grant-modal-desc',
          list: [
            'verification-euipo-grant-modal-list-item-0',
            'verification-euipo-grant-modal-list-item-1',
            'verification-euipo-grant-modal-list-item-2',
          ],
        },
      },
    },
    trademarkType: {
      title: 'verification-trademark-type-title',
      options: {
        [VerificationTrademarkTypeValue.logo]: {
          imageSrc: '/images/form/nike-logo.svg',
          imageAlt: 'Nike logo - logo trademark placeholder',
          title: 'verification-logo-type-title',
          subtitle: 'verification-logo-type-subtitle',
          description: 'verification-logo-type-desc',
          type: TrademarkType.image,
          dataTest: 'logoTrademarkType',
        },
        [VerificationTrademarkTypeValue.image]: {
          imageSrc: '/images/form/nike-swoosh.svg',
          imageAlt: 'Nike swoosh - figurative trademark placeholder',
          title: 'verification-figurative-type-title',
          subtitle: 'verification-figurative-type-subtitle',
          description: 'verification-figurative-type-desc',
          type: TrademarkType.image,
          dataTest: 'figurativeTrademarkType',
        },
        [VerificationTrademarkTypeValue.text]: {
          imageSrc: '/images/form/nike-name.svg',
          imageAlt: 'Nike name - word trademark placeholder',
          title: 'verification-word-type-title',
          subtitle: 'verification-word-type-subtitle',
          description: 'verification-word-type-desc',
          type: TrademarkType.text,
          dataTest: 'wordTrademarkType',
        },
      },
    },
    dropzone: {
      logoTitle: 'verification-dropzone-logo-tm-title',
      figurativeTitle: 'verification-dropzone-figurative-tm-title',
      description: 'verification-trademark-dropzone-desc',
      supportedFormats: 'verification-trademark-dropzone-supported-formats',
    },
    wordTrademark: {
      title: 'verification-word-type-wording-title',
      placeholder: 'verification-word-type-wording-placeholder',
      dataTest: 'wordTrademarkDefinition',
    },
    serviceDescription: {
      title: 'verification-gs-title',
      description: 'verification-gs-desc-with-tips',
      descriptionNew: 'verification-gs-desc-with-tips-new',
      gsLabel: 'verification-gs-label',
      gsPlaceholder: 'verification-gs-placeholder',
      websiteLabel: 'verification-website-custom-label',
      websiteExamples: 'verification-website-examples',
      dataTest: 'serviceDescription',
      gsTips: {
        title: 'verification-gs-tips-title',
        description: 'verification-gs-tips-description',
        categories: [
          {
            title: 'verification-gs-tips-category-0-title',
            description: 'verification-gs-tips-category-0-description',
            examples: [
              {
                badExample: {title: 'verification-gs-tips-category-0-bad-example-0'},
                goodExample: {title: 'verification-gs-tips-category-0-good-example-0'},
              },
              {
                goodExample: {title: 'verification-gs-tips-category-0-good-example-1'},
              },
            ],
          },
          {
            title: 'verification-gs-tips-category-1-title',
            description: 'verification-gs-tips-category-1-description',
            examples: [
              {
                badExample: {title: 'verification-gs-tips-category-1-bad-example-0'},
                goodExample: {title: 'verification-gs-tips-category-1-good-example-0'},
              },
              {
                badExample: {title: 'verification-gs-tips-category-1-bad-example-1'},
                goodExample: {title: 'verification-gs-tips-category-1-good-example-1'},
              },
              {
                badExample: {title: 'verification-gs-tips-category-1-bad-example-2'},
                goodExample: {title: 'verification-gs-tips-category-1-good-example-2'},
              },
            ],
          },
          {
            title: 'verification-gs-tips-category-2-title',
            description: 'verification-gs-tips-category-2-description',
            examples: [
              {
                badExample: {title: 'verification-gs-tips-category-2-bad-example-0'},
                goodExample: {title: 'verification-gs-tips-category-2-good-example-0'},
              },
            ],
          },
        ],
      },
    },
    contactDetails: {
      title: 'verification-contact-details-title',
      firstNameLabel: 'contact-name',
      firstNamePlaceholder: 'contact-name-placeholder',
      lastNameLabel: 'contact-surname',
      lastNamePlaceholder: 'contact-surname-placeholder',
      emailLabel: 'contact-email',
      emailPlaceholder: 'contact-email-placeholder',
      phoneLabel: 'verification-contact-phone-label',
      phoneNote: 'verification-contact-sms-desc',
      skipVerification:
        'Skip verification - this will automatically approve the verification (with everything set to fully-met), without notifying customer about the verification process through email or sms',
      marketingConsentText: 'marketing-consent-text',
      consentNote: {
        text: 'ver-form-consent-note',
        linkHrefs: [routeTo.termsAndConditions()],
      },
      loggedInClientDescription: {
        text: 'verification-contact-logged-in-client-desc',
        linkHref: routeTo.portal.profile(),
      },
    },
    note: {
      title: 'verification-note-title',
      description: 'verification-note-description',
      label: 'note-label',
      optionalLabel: 'note-optional-label',
      placeholder: 'note-placeholder',
      returningClientButtonText: 'verification-note-returning-client-title',
    },
    reassuranceBanner: {
      introDescription: 'verification-intro-reassurance-desc',
      introDescriptionReturningClient: 'verification-intro-reassurance-desc-returning-client',
      introDescriptionReturningClientWithName:
        'verification-intro-reassurance-desc-returning-client-with-name',
      contactDescription: 'verification-contact-reassurance-desc',
    },
    summary: {
      title: 'verification-cart-summary-title',
      benefit: {
        title: 'verification-cart-benefit-title',
        description: 'verification-cart-benefit-desc',
      },
    },
  }
}

export const getVerificationFormSuccessPropsContent = (language: Locale) => {
  return {
    title: 'verification-success-title',
    descriptions: ['verification-success-desc-0', 'verification-success-desc-1'],
    timeline: {
      title: 'verification-success-timeline-title',
      subtitle: 'verification-success-timeline-subtitle',
      steps: [
        {
          imageSrc: `/images/icons/your-brand-in-square-${language}.svg`,
          imageAlt: 'Your brand',
          title: 'verification-success-timeline-step-0-title',
          status: SuccessPageTimelineStepStatus.completed,
        },
        {
          imageSrc: '/images/icons/trademark-symbol-in-square.svg',
          imageAlt: 'Trademark symbol in a square',
          title: 'verification-success-timeline-step-1-title',
          description: 'verification-success-timeline-step-1-desc',
          linkHref: routeTo.howItWorks(),
          linkText: 'learn-more-text',
          trackedEvent: {
            name: 'Learn more is clicked',
            parameters: {page: 'Success verification', step: 'Trademark registration'},
          },
          status: SuccessPageTimelineStepStatus.current,
        },
        {
          imageSrc: '/images/icons/shield-with-star-in-square.svg',
          imageAlt: 'Shield with a star in a square',
          title: 'verification-success-timeline-step-2-title',
          description: 'verification-success-timeline-step-2-desc',
          linkHref: routeTo.brandMonitoring.home(),
          linkText: 'learn-more-text',
          trackedEvent: {
            name: 'Learn more is clicked',
            parameters: {page: 'Success verification', step: 'Brand protection'},
          },
          status: SuccessPageTimelineStepStatus.disabled,
        },
      ],
    },
    emailNotReceivedDescription: 'success-email-not-received-desc',
    spamDescription: 'success-spam-desc',
    cpButtonText: 'success-page-registration-detail-button',
  }
}
